import React, { useEffect, useState } from 'react';
import { FaPlaneArrival, FaDesktop, FaTimes } from 'react-icons/fa';
import * as turf from '@turf/turf';
import { FaPlaneUp } from 'react-icons/fa6';

const EFRO_COORDS = [25.8476, 66.5608]; // [lon, lat]

const translations = {
  en: {
    title: 'Arrivals',
    time: 'Time',
    origin: 'Origin',
    airline: 'Airline',
    flight: 'Flight',
    status: 'Status',
  },
  fi: {
    title: 'Saapuvat',
    time: 'Aika',
    origin: 'Lähtöpaikka',
    airline: 'Lentoyhtiö',
    flight: 'Lento',
    status: 'Lennon tila',
  },
  sv: {
    title: 'Ankomster',
    time: 'Tid',
    origin: 'Från',
    airline: 'Flygbolag',
    flight: 'Flyg',
    status: 'Status',
  },
};

const statusTranslations = {
  en: {
    enroute: 'Enroute',
    arrived: 'Arrived',
    approaching: 'Approaching',
    departing: 'Departing',
    delivering: 'Delivering',
  },
  fi: {
    enroute: 'Matkalla',
    arrived: 'Laskeutunut',
    approaching: 'Lähestyy',
    departing: 'Lähdössä',
    delivering: 'Toimittaa',
  },
  sv: {
    enroute: 'På väg',
    arrived: 'Anlänt',
    approaching: 'Ankommer',
    departing: 'Avgår',
    delivering: 'Levererar',
  },
};

const FlightBoard = ({ trafficData = [] }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [airlineCache, setAirlineCache] = useState({});
  const [airportCache, setAirportCache] = useState({});

  const toggleRoutesMenu = () => setIsVisible(!isVisible);
  const urlStart = 'https://gid-backend.lusep.fi'; // https://gid-backend.lusep.fi http://localhost:5000

  const [langIndex, setLangIndex] = useState(0);
  const [utcTime, setUtcTime] = useState('');
  const langs = ['en', 'fi', 'sv'];

  // Rotate languages every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLangIndex((prev) => (prev + 1) % langs.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Update UTC time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hh = String(now.getUTCHours()).padStart(2, '0');
      const mm = String(now.getUTCMinutes()).padStart(2, '0');
      setUtcTime(`${hh}:${mm}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const t = translations[langs[langIndex]];

  // Fetch airline info and cache it
  const fetchAirline = async (icao) => {
    if (!icao) return null;
    if (airlineCache[icao]) return airlineCache[icao];
    try {
      const searchIcao = icao === 'NOZ' ? 'NSZ' : icao;
      const res = await fetch(
        `${urlStart}/api/airlines/search/icao?q=${searchIcao}`,
      );
      const data = await res.json();
      if (data && data.length > 0) {
        const airline = data[0];

        const logo = `${urlStart}/api/airline-logo/${airline.iata}`;
        const obj = { ...airline, logo };
        setAirlineCache((prev) => ({ ...prev, [icao]: obj }));
        return obj;
      }
    } catch (e) {
      console.error('Airline fetch failed', e);
    }
    return null;
  };

  const fetchAirport = async (icao) => {
    if (!icao) return null;
    if (airportCache[icao]) return airportCache[icao];

    try {
      const res = await fetch(`${urlStart}/api/airports/search/icao?q=${icao}`);
      const data = await res.json();

      if (data && data.length > 0) {
        const municipality = data[0].municipality || icao;
        setAirportCache((prev) => ({ ...prev, [icao]: municipality }));
        return municipality;
      }
      return icao;
    } catch (error) {
      console.error('Error fetching airport:', error);
      return icao;
    }
  };

  const calculateArrivalTime = (deptime, enroute_time) => {
    if (!deptime || !enroute_time) return null;

    const totalMinutes =
      parseInt(deptime.slice(0, 2)) * 60 +
      parseInt(deptime.slice(2, 4)) +
      parseInt(enroute_time.slice(0, 2)) * 60 +
      parseInt(enroute_time.slice(2, 4));

    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;

    return (
      `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}` + (totalMinutes >= 1440 ? '' : '')
    );
  };

  const [arrivals, setArrivals] = useState([]);

  useEffect(() => {
    const processFlights = async () => {
      const flights = await Promise.all(
        trafficData
          .filter((f) => f.flight_plan?.arrival === 'EFRO')
          .map(async (f) => {
            const from = turf.point([f.longitude, f.latitude]);
            const to = turf.point(EFRO_COORDS);
            const distance = turf.distance(from, to, {
              units: 'nauticalmiles',
            });

            let status = 'enroute';
            if (f.altitude < 1500 && distance < 3) {
              status = 'arrived';
            } else if (distance < 40) {
              status = 'approaching';
            } else if (f.altitude < 1500) {
              status = 'departing';
            }

            const airlineIcao = f.callsign?.substring(0, 3) || '';
            const flightNum =
              f.callsign?.replace(airlineIcao, '') || f.callsign;

            let airline = await fetchAirline(airlineIcao);
            const origin =
              (await fetchAirport(f.flight_plan?.departure)) ||
              f.flight_plan?.departure ||
              'Unknown';
            const arrivalTime =
              calculateArrivalTime(
                f.flight_plan?.deptime,
                f.flight_plan?.enroute_time,
              ) || 'Unknown';

            return {
              ...f,
              distance,
              status,
              airlineIcao,
              flightNum,
              airline,
              origin,
              arrivalTime,
            };
          }),
      );

      flights.sort((a, b) => {
        const statusPriority = {
          Arrived: 0,
          Approaching: 1,
          Enroute: 2,
          Departing: 3,
        };

        const aStatusPriority = statusPriority[a.status] ?? 99;
        const bStatusPriority = statusPriority[b.status] ?? 99;

        // First sort by status priority
        if (aStatusPriority !== bStatusPriority) {
          return aStatusPriority - bStatusPriority;
        }

        // If both are "Approaching", sort by altitude ascending (lowest first)
        if (a.status === 'Approaching' && b.status === 'Approaching') {
          return a.altitude - b.altitude;
        }

        // For "Enroute" and "Departing", sort by distance ascending
        return a.distance - b.distance;
      });

      setArrivals(flights);
    };

    processFlights();
  }, [trafficData]);

  return (
    <div>
      {/* Toggle Button */}
      <button
        className="flex items-center justify-center fixed bottom-[120px] right-[12px] w-11 h-11 p-2 bg-blue-900 border border-gray-700 rounded-full shadow-lg hover:bg-blue-800 transition"
        onClick={toggleRoutesMenu}
      >
        <FaPlaneArrival className={'text-lg text-gray-300'} />
      </button>

      {isVisible && (
        <div className="fixed inset-0 flex items-center justify-center z-40 bg-black bg-opacity-50">
          <div className="relative w-[700px] max-w-[90%] max-h-[80vh] overflow-y-auto bg-gradient-to-br from-slate-900 to-gray-950 border border-gray-800 shadow-xl p-6 text-white">
            {/* Close Button */}
            <button
              className="absolute top-3 right-3 text-gray-300 text-lg hover:bg-red-600 font-bold rounded-full bg-red-700 w-7 h-7 flex items-center justify-center p-0"
              onClick={toggleRoutesMenu}
            >
              <FaTimes />
            </button>
            <h3 className="text-xl font-bold mb-4 border-b border-gray-700 pb-2 text-center">
              <span className="flex items-center gap-2 text-gray-300">
                <FaDesktop className={'text-yellow-500 text-lg'} /> Rovaniemi
                Flights
              </span>
            </h3>

            <div className={'flex items-center gap-4 text-gray-300 mb-2'}>
              <div className="text-xl pl-1 font-medium">{utcTime}</div>
              <div className="text-xl pl-1 font-medium min-w-[120px] flex items-center gap-2">
                <FaPlaneArrival className={'text-lg'} />
                {t.title}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse text-sm text-gray-300">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 pl-1 font-medium">
                      {t.time}
                    </th>
                    <th className="text-left py-2 pl-2 font-medium">
                      {t.origin}
                    </th>
                    <th className="text-left py-2 pl-4 min-w-[100px] font-medium">
                      {t.airline}
                    </th>
                    <th className="text-left py-2 pl-4 font-medium">
                      {t.flight}
                    </th>
                    <th className="text-left py-2 pl-2 font-medium"></th>
                    <th className="text-left pl-6 w-full font-medium">
                      {t.status}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr className="bg-slate-800">
                    <td className=" pl-2 pr-1 text-gray-300 font-semibold text-xs">
                      20:25
                    </td>
                    <td className="py-2 pl-2 text-yellow-500 font-semibold min-w-[100px]">
                      Korvatunturi
                    </td>
                    <td className="pl-2">
                      <div className="flex items-center justify-center h-full max-w-[80px] pl-2">
                        <img
                          src={'img/santa.png'}
                          alt={'Santa'}
                          className="object-contain min-h-[24px] max-h-[24px] p-0.5 w-full bg-white"
                        />
                      </div>
                    </td>
                    <td className="pl-4 pr-2 font-semibold text-white">
                      SANTA25
                    </td>
                    <td className="pl-2 pr-2 font-semibold text-white">ULAC</td>
                    <td className="pl-6 pr-4 text-left text-gray-300 font-semibold text-nowrap">
                      {statusTranslations[langs[langIndex]]['delivering']}
                    </td>
                  </tr>
                  {arrivals.map((flight, idx) => {
                    const airlineName = flight.airline?.name || '';
                    let logo = flight.airline?.logo || null;

                    if (logo && logo.endsWith('/')) {
                      logo = null;
                    }

                    return (
                      <tr
                        key={idx}
                        className="odd:bg-slate-800 even:bg-slate-700"
                      >
                        <td className="py-2 pl-2 pr-1 text-gray-300 font-semibold text-xs">
                          {flight.arrivalTime}
                        </td>

                        <td className="pl-2 text-yellow-500 font-semibold text-nowrap overflow-hidden min-w-[100px] max-w-[180px]">
                          {flight.origin}
                        </td>

                        <td className={'pl-2'}>
                          {logo ? (
                            <div className="flex items-center justify-center h-full max-w-[80px] pl-2">
                              <img
                                src={logo}
                                alt={airlineName}
                                className="object-contain max-h-[24px] p-0.5 w-full bg-white"
                              />
                            </div>
                          ) : (
                            <p className="text-xs max-w-[80px] font-semibold text-gray-200 p-1 text-nowrap overflow-hidden">
                              {airlineName}
                            </p>
                          )}
                        </td>

                        <td className="pl-4 pr-2 font-semibold text-white">
                          {flight.callsign}
                        </td>

                        <td className="pl-2 pr-2 font-semibold text-white">
                          {flight.flight_plan.aircraft_short}
                        </td>

                        <td className="pl-6 pr-4 text-left text-gray-300 font-semibold">
                          {statusTranslations[langs[langIndex]][flight.status]}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightBoard;
